<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConnectedApplicationsAPI from 'dashboard/api/connectedApplications';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();

const applications = ref([]);
const isLoading = ref(true);
const revokingId = ref(null);

// O escopo é texto de protocolo: "poweragents:publish" não conta a ninguém que
// isso muda o agente com que o cliente conversa. Sem tradução, a tela lista sem
// informar — o contrário do motivo de ela existir. As chaves são literais de
// propósito: é a convenção do repositório, e chave montada em runtime não é
// encontrada por quem for procurar de onde vem o texto.
const scopeLabel = scope => {
  switch (scope) {
    case 'powerdata:read':
      return t(
        'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.SCOPE_LABELS.POWERDATA_READ'
      );
    case 'powerdata:write':
      return t(
        'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.SCOPE_LABELS.POWERDATA_WRITE'
      );
    case 'poweragents:read':
      return t(
        'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.SCOPE_LABELS.POWERAGENTS_READ'
      );
    case 'poweragents:write':
      return t(
        'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.SCOPE_LABELS.POWERAGENTS_WRITE'
      );
    case 'poweragents:test':
      return t(
        'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.SCOPE_LABELS.POWERAGENTS_TEST'
      );
    case 'poweragents:publish':
      return t(
        'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.SCOPE_LABELS.POWERAGENTS_PUBLISH'
      );
    case 'powerflow:run':
      return t(
        'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.SCOPE_LABELS.POWERFLOW_RUN'
      );
    // Escopo novo que a tela ainda não conhece aparece cru, em vez de sumir:
    // esconder um acesso concedido é justamente o que não pode acontecer aqui.
    default:
      return scope;
  }
};

const formatDate = value =>
  value
    ? new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : '';

const fetchApplications = async () => {
  isLoading.value = true;
  try {
    const { data } = await ConnectedApplicationsAPI.get();
    applications.value = data;
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const revoke = async application => {
  revokingId.value = application.id;
  try {
    await ConnectedApplicationsAPI.revoke(application.id);
    applications.value = applications.value.filter(
      item => item.id !== application.id
    );
    useAlert(
      t('PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.REVOKED', {
        name: application.name,
      })
    );
  } catch (error) {
    // Recarrega em vez de só avisar: se a revogação falhou, a lista na tela
    // pode não ser mais a verdade, e uma tela que mente sobre quem tem acesso
    // é pior do que uma que erra ao carregar.
    useAlert(
      t('PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.REVOKE_ERROR')
    );
    fetchApplications();
  } finally {
    revokingId.value = null;
  }
};

onMounted(fetchApplications);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="isLoading" class="flex justify-center py-4">
      <Spinner />
    </div>
    <p v-else-if="!applications.length" class="mb-0 text-sm text-n-slate-11">
      {{ $t('PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.EMPTY') }}
    </p>
    <div v-else class="flex flex-col">
      <div
        v-for="application in applications"
        :key="application.id"
        class="flex flex-row items-start justify-between gap-4 py-3 border-b border-n-weak last:border-b-0"
      >
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{ application.name }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ application.scopes.map(scopeLabel).join(' · ') }}
          </span>
          <span
            v-if="application.authorized_at"
            class="text-xs text-n-slate-10"
          >
            {{
              $t(
                'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.AUTHORIZED_ON',
                { date: formatDate(application.authorized_at) }
              )
            }}
          </span>
        </div>
        <ConfirmButton
          :label="
            $t('PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.REVOKE')
          "
          :confirm-label="
            $t(
              'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.CONFIRM_REVOKE'
            )
          "
          :confirm-hint="
            $t(
              'PROFILE_SETTINGS.FORM.CONNECTED_APPLICATIONS_SECTION.CONFIRM_HINT',
              { name: application.name }
            )
          "
          :is-loading="revokingId === application.id"
          color="slate"
          confirm-color="ruby"
          variant="outline"
          icon="i-lucide-unplug"
          class="rounded-xl shrink-0"
          @click="revoke(application)"
        />
      </div>
    </div>
  </div>
</template>
